import React, { useState, useEffect } from 'react';
import { Alert, Box, TextField, FormControl, InputLabel, Select, MenuItem, Typography, Stepper, Step, StepLabel } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import 'dayjs/locale/en-gb';

dayjs.extend(utc);
import ProjectCard from '../components/ProjectCard';
import { isRadarSourced } from '../../../constants/employmentSourceSystems';
import { employeesApi } from '../../../services/api';
import LoadingSpinner from '../../common/LoadingSpinner';
import { useAlert } from '../../../contexts/AlertContext';
import { EMPLOYEE_STATUSES } from '../../../constants/employeeStatuses';
import { RESIGN_REASONS, RESIGN_REASON_OPTIONS } from '../../../constants/resignReasons';
import UpcomingEmployeeEvents from '../components/UpcomingEmployeeEvents';
import { previewEndEmployment, resignationRefusal } from '../../../utils/endEmploymentPreview';
import { renewalSwitchDay } from '../../../constants/employmentEventAnnotations';
import { toPickerValue } from '../../../utils/dateOnly';

const NO_CONTRACTS = [];
const dmy = (value) => (value ? dayjs.utc(value).format('DD/MM/YYYY') : null);

/**
 * ADR-185 §8 — the contracts this resignation ends, on their own step (only
 * when the person holds more than one): what happens to each at the chosen
 * date, and what it carries along (its scheduled replacement, its owner).
 */
const ReviewContracts = ({ rows, refusal }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    <Typography variant="body1" sx={{ color: '#64748B' }}>
      Resignation ends the employment — <strong>every contract this consultant holds</strong>.
      This is what happens to each on the resignation date.
    </Typography>
    {refusal && <Alert severity="error">{refusal}</Alert>}
    {rows.map((row) => (
      <Box key={row.id} sx={{ border: '1px solid #e2e8f0', borderRadius: '4px', p: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {row.label}{row.isPrimary ? ' (primary)' : ''}
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5 }}>{row.outcome}</Typography>
        {row.notes?.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
            {row.notes.map((note) => (
              <Alert key={note.text} severity={note.severity}>{note.text}</Alert>
            ))}
          </Box>
        )}
      </Box>
    ))}
  </Box>
);

// ADR-185 — Resign is a PERSON verb (ADR-149): `contracts` are the person's
// open contracts; with more than one the wizard gains the Review Contracts
// step (`showContractsStep`, decided by the host so its buttons agree).
const EmployeeResign = ({ employee, contracts = NO_CONTRACTS, showContractsStep = false, onValidChange, onStepChange }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    resignationDate: null,
    resignationReason: '',
    resignationReasonDetails: ''
  });

  const [touched, setTouched] = useState({
    resignationDate: false,
    resignationReason: false,
    resignationReasonDetails: false
  });

  const [employeeDetails, setEmployeeDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { showAlert } = useAlert();

  const steps = showContractsStep
    ? ['Resignation Details', 'Review Contracts', 'Review Projects']
    : ['Resignation Details', 'Review Projects'];
  const contractsStep = showContractsStep ? 1 : -1;
  const projectsStep = steps.length - 1;

  const handleDateChange = (newValue) => {
    const newDate = newValue && typeof newValue.isValid === 'function' && newValue.isValid()
      ? newValue.format('YYYY-MM-DD')
      : null;

    const newFormData = {
      ...formData,
      resignationDate: newDate
    };
    setFormData(newFormData);
    setTouched(prev => ({
      ...prev,
      resignationDate: true
    }));

    // Valid if both date and reason are provided, and if "Other" is selected, details must be provided
    const isValid = newDate && formData.resignationReason &&
      (formData.resignationReason !== RESIGN_REASONS.OTHER || (formData.resignationReason === RESIGN_REASONS.OTHER && formData.resignationReasonDetails));
    onValidChange(isValid, newFormData);
  };

  const handleInputChange = (field) => (event) => {
    let newFormData = {
      ...formData,
      [field]: event.target.value
    };

    // Clear resignationReasonDetails if changing from "Other" to another reason
    if (field === 'resignationReason' && formData.resignationReason === RESIGN_REASONS.OTHER && event.target.value !== RESIGN_REASONS.OTHER) {
      newFormData.resignationReasonDetails = '';
    }

    setFormData(newFormData);
    setTouched(prev => ({
      ...prev,
      [field]: true
    }));

    // Valid if both date and reason are provided, and if "Other" is selected, details must be provided
    const isValid = newFormData.resignationDate && newFormData.resignationReason &&
      (newFormData.resignationReason !== RESIGN_REASONS.OTHER || (newFormData.resignationReason === RESIGN_REASONS.OTHER && newFormData.resignationReasonDetails));
    onValidChange(isValid, newFormData);
  };

  const shouldShowError = (field) => {
    return touched[field];
  };

  const handleNext = () => {
    const newStep = activeStep + 1;
    setActiveStep(newStep);
    if (onStepChange) {
      onStepChange(newStep);
    }
  };

  const handleBack = () => {
    const newStep = activeStep - 1;
    setActiveStep(newStep);
    if (onStepChange) {
      onStepChange(newStep);
    }
  };

  React.useEffect(() => {
    // Initialize as invalid
    onValidChange(false, formData);
  }, []);

  // Fetch employee details when moving to the projects step
  useEffect(() => {
    if (activeStep === projectsStep && !employeeDetails) {
      fetchEmployeeDetails();
    }
  }, [activeStep, employee.id, projectsStep]);

  const fetchEmployeeDetails = async () => {
    try {
      setIsLoading(true);
      const details = await employeesApi.getEmployeeById(employee.id);
      setEmployeeDetails(details);
    } catch (err) {
      showAlert('Failed to fetch employee details', 'error', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Expose the step navigation methods to parent
  React.useEffect(() => {
    if (onStepChange) {
      onStepChange(activeStep, { handleNext, handleBack });
    }
  }, [activeStep]);

  // ADR-185 — what this resignation does to each contract at the chosen date.
  // With one contract (or none listed) the primary is described from the
  // person, as the dialog always did.
  const describedContracts = contracts.length > 0
    ? contracts
    : [{ id: 'primary', isPrimary: true, startDate: employee?.hireDate, pendingRenewal: employee?.pendingRenewal || null, source: employee?.currentEpisodeSource || null }];
  const contractRows = previewEndEmployment({ kind: 'RESIGN', contracts: describedContracts, resignationDate: formData.resignationDate });
  const refusal = resignationRefusal({ contracts, resignationDate: formData.resignationDate });
  const ownedElsewhere = [...new Set(describedContracts
    .map((c) => c.source)
    .filter((source) => !isRadarSourced({ source })))];
  const singleRenewal = !showContractsStep ? describedContracts[0]?.pendingRenewal : null;
  const singleRenewalNote = !showContractsStep && formData.resignationDate
    ? contractRows[0]?.notes?.find((note) => note.kind === 'replacement')
    : null;

  const activeProjects = employeeDetails?.projects?.filter(p =>
      [
        EMPLOYEE_STATUSES.ENGAGED,
        EMPLOYEE_STATUSES.JOINING,
        EMPLOYEE_STATUSES.LEAVING
      ].includes(p.employeeData?.projectStatus?.toLowerCase())
  ) || [];

  return (
    <Box sx={{ width: '100%' }}>
      <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {activeStep === 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Alert severity="info">
            This <strong>schedules the end of employment</strong> on the chosen date. The
            person remains employed until then and the resignation can still be withdrawn;
            once the date passes, the termination is enacted — their projects, assignments
            and histories are closed as of that date. To record a <strong>fixed-term
            contract end</strong> rather than a departure, use <em>Add End Date</em> instead.
            {showContractsStep && (
              <>
                {' '}This ends <strong>all {contracts.length} contracts</strong> this consultant
                holds — each is shown on the next step, <em>Review Contracts</em>.
              </>
            )}
          </Alert>
          {/* ADR-146 §2 — warn (never block) on an ingestion-owned contract:
              a scheduled end the source system does not carry is withdrawn by
              its next run. A genuine leaver must be recorded there too. */}
          {/* ADR-185 D5 — every open contract's owner, not only the primary's */}
          {ownedElsewhere.length > 0 && (
            <Alert severity="warning">
              {showContractsStep ? 'A contract this ends is' : 'This contract is'} <strong>owned by an
              ingestion source ({ownedElsewhere.join(', ')})</strong>. A resignation date that is not
              also recorded in the source system will be <strong>withdrawn by its next
              sync run</strong> — record the departure there too, or it will not stick.
            </Alert>
          )}
          {/* ADR-136 §15 — fresh-fetched standing schedule, independent of the
              (possibly stale) list payload the card menu gated on. ADR-185 D3:
              PERSON-WIDE for Resign (it ends every contract), grouped by
              contract, and saying what the chosen date does to each item. */}
          <UpcomingEmployeeEvents
            employeeId={employee?.id}
            contracts={contracts}
            resignationDate={formData.resignationDate}
          />
          {/* ADR-185 D6 — a resignation cancels a scheduled replacement that would
              switch after the person has left; one switching before goes ahead
              and the new contract ends on the resignation date too (§10).
              With more than one contract this is said per contract on the
              Review Contracts step. */}
          {singleRenewal?.effectiveOn && (
            singleRenewalNote ? (
              <Alert severity={singleRenewalNote.severity}>{singleRenewalNote.text}</Alert>
            ) : !formData.resignationDate && (
              <Alert severity="info">
                A contract replacement is <strong>scheduled — switch
                on {dmy(renewalSwitchDay(singleRenewal.effectiveOn))}</strong>. A resignation dated on or
                before {dmy(singleRenewal.effectiveOn)} cancels it; with a later date it still switches,
                and the new contract it starts <strong>ends on the resignation date too</strong>.
              </Alert>
            )
          )}
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
            <DatePicker
              label="Resignation Date"
              value={toPickerValue(formData.resignationDate)}
              onChange={handleDateChange}
              minDate={toPickerValue(employee.hireDate) ?? undefined}
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: true,
                  error: shouldShowError('resignationDate') && !formData.resignationDate,
                  helperText: shouldShowError('resignationDate') && !formData.resignationDate
                    ? 'Resignation date is required'
                    : 'The date when the employee will leave',
                  inputProps: {
                    placeholder: 'DD/MM/YYYY'
                  }
                }
              }}
            />
          </LocalizationProvider>

          <FormControl
            fullWidth
            required
            error={shouldShowError('resignationReason') && !formData.resignationReason}
          >
            <InputLabel>Resignation Reason</InputLabel>
            <Select
              value={formData.resignationReason}
              onChange={handleInputChange('resignationReason')}
              label="Resignation Reason"
            >
              {RESIGN_REASON_OPTIONS.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {shouldShowError('resignationReason') && !formData.resignationReason && (
              <Box component="span" sx={{ color: 'error.main', fontSize: '0.75rem', mt: 0.5, ml: 2 }}>
                Resignation reason is required
              </Box>
            )}
          </FormControl>

          {formData.resignationReason === RESIGN_REASONS.OTHER && (
            <TextField
              fullWidth
              label="Specify Reason"
              value={formData.resignationReasonDetails}
              onChange={handleInputChange('resignationReasonDetails')}
              multiline
              rows={4}
              required
              error={shouldShowError('resignationReasonDetails') && !formData.resignationReasonDetails}
              helperText={shouldShowError('resignationReasonDetails') && !formData.resignationReasonDetails
                ? 'Please specify the resignation reason'
                : ''}
              placeholder="Please specify the resignation reason..."
            />
          )}

          {/* ADR-185 D2 — the server's refusal, before the operator goes on */}
          {refusal && <Alert severity="error">{refusal}</Alert>}
        </Box>
      )}

      {activeStep === contractsStep && (
        <ReviewContracts rows={contractRows} refusal={refusal} />
      )}

      {activeStep === projectsStep && (
        <Box>
          {isLoading ? (
            <LoadingSpinner />
          ) : (
            <>
              <Typography variant="body1" sx={{ mb: 2, color: '#64748B' }}>
                Review the employee's active projects. These projects will be marked as ending on the resignation date.
              </Typography>

              {activeProjects.length === 0 ? (
                <Box sx={{
                  p: 3,
                  textAlign: 'center',
                  color: '#64748B',
                  border: '1px dashed #cbd5e1',
                  borderRadius: '4px'
                }}>
                  <Typography variant="body2">
                    No active projects found for this employee.
                  </Typography>
                </Box>
              ) : (
                <>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>{activeProjects.length}</strong> {activeProjects.length === 1 ? 'project' : 'projects'} found
                    </Typography>
                  </Box>
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: 'repeat(2, 1fr)',
                      md: 'repeat(2, 1fr)',
                    },
                    gap: '24px',
                    mb: 3
                  }}>
                    {activeProjects.map(project => (
                      <ProjectCard key={project.id} project={project} />
                    ))}
                  </Box>
                </>
              )}
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

export default EmployeeResign;
